import React from "react"
import {useAccessToken} from "../../services/auth"
import {AsyncResultModal} from "../AsyncResultModal/AsyncResultModal"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {BeneficialOwner, BeneficialOwnerResource} from "../../resources/beneficialOwner"
import {CustomerBeneficialOwner} from "../../resources/customerBeneficialOwner"
import {Formik} from "formik"
import * as Yup from "yup"
import LeftRightSlots from "../../containers/LeftRightSlots/LeftRightSlots"
import beneficialOwnerValidationSchema from "./beneficialOwnerValidationSchema"
import {US_COUNTRY_CODE} from "../../resources/address"
import {toPhoneNumber} from "../../resources/common"
import AddressComponent, {focusFirstInvalidField} from "../Address/Address"
import {AddToast, useToasts} from "react-toast-notifications"
import Textfield from "../Address/Textfield"
import IdentificationTypeSelect from "../IdentificationTypeSelect/ApplicationIdentificationNumber"
import DateField from "../DateField/DateField"
import PhoneNumber from "../PhoneNumber/PhoneNumber"
import {Result} from "neverthrow"
import {ErrorDocument, OkDocument} from "../../resources/common"
import moment from "moment"

export type Tag = {key: string; value: string}

interface ModalContentLeftSideProps {
	values: {
		firstName: string
		lastName: string
		ssn: string
		nationality: string
		passport: string
		matriculaConsular: string
		dateOfBirth: Date | string
		email: string
		phone: string
		percentage: number
		identificationType: string
	}
	handleChange: any
}

const ModalContentLeftSide = ({values, handleChange}: ModalContentLeftSideProps) => {
	return (
		<>
			<div className="beneficial-owner-section-title">Beneficial Owner details</div>
			<div className="field-row-wrapper">
				<Textfield
					label="First Name"
					placeholder="Add first name"
					name="firstName"
					value={values.firstName}
					onChange={handleChange}
					onBlur={() => null}
				/>
				<Textfield
					label="Last Name"
					placeholder="Add last name"
					name="lastName"
					value={values.lastName}
					onChange={handleChange}
					onBlur={() => null}
				/>
			</div>
			<div className="field-row-wrapper">
				<IdentificationTypeSelect value={values.identificationType} onChange={handleChange} label="Document Type" />
				{values.identificationType === "passport" ? (
					<>
						<Textfield
							label="Nationality"
							placeholder="Add country"
							name="nationality"
							value={values.nationality}
							onChange={handleChange}
							onBlur={() => null}
						/>
						<Textfield
							label="Document ID #"
							placeholder="Enter Passport"
							name="passport"
							value={values.passport}
							onChange={handleChange}
							onBlur={() => null}
						/>
					</>
				) : null}
				{values.identificationType === "ssn" ? (
					<>
						<Textfield
							label="Nationality"
							placeholder="Add country"
							name="nationality"
							value={values.nationality}
							onChange={handleChange}
							onBlur={() => null}
						/>
						<Textfield
							label="Document ID #"
							placeholder="Enter SSN"
							name="ssn"
							value={values.ssn}
							onChange={handleChange}
							onBlur={() => null}
						/>
					</>
				) : null}
				{values.identificationType === "matriculaConsular" ? (
					<Textfield
						label="Document ID #"
						placeholder="Enter Matricula Consular"
						name="matriculaConsular"
						value={values.matriculaConsular}
						onChange={handleChange}
						onBlur={() => null}
					/>
				) : null}
			</div>
			<div className="field-row-wrapper">
				<div className="date-of-birth-container">
					<DateField
						name="dateOfBirth"
						label="Add Date of birth"
						value={values.dateOfBirth as string}
						onChange={handleChange}
						onBlur={() => null}
					/>
				</div>
				<Textfield
					label="Percentage"
					placeholder="Add Percentage"
					name="percentage"
					value={values.percentage}
					onChange={handleChange}
					onBlur={() => null}
				/>
			</div>
			<div className="field-row-wrapper">
				<PhoneNumber value={values.phone} handleChange={handleChange} />
				<Textfield
					label="Email"
					placeholder="Add Email"
					name="email"
					value={values.email}
					onChange={handleChange}
					onBlur={() => null}
				/>
			</div>
		</>
	)
}

interface ModalContentRightSideProps {
	values: {
		street: string
		street2: string
		city: string
		country: string | undefined
		state: string
		postalCode: string
	}
	handleChange: any
	handleBlur: any
	setFieldValue: Function
}
const ModalContentRightSide = ({values, handleChange, handleBlur, setFieldValue}: ModalContentRightSideProps) => (
	<>
		<div className="beneficial-owner-section-title">Address</div>
		<div className="field-row-wrapper">
			<div className="edit-beneficial-owner-address">
				<AddressComponent
					closed={false}
					street={values.street}
					street2={values.street2}
					city={values.city}
					country={values.country || ""}
					state={values.state}
					postalCode={values.postalCode}
					isUsOnlyAddress={false}
					handleChange={handleChange}
					handleBlur={handleBlur}
					setFieldValue={setFieldValue}
				/>
			</div>
		</div>
	</>
)

interface ModalProps {
	fullName: BeneficialOwner["fullName"]
	ssn?: BeneficialOwner["ssn"]
	passport?: BeneficialOwner["passport"]
	matriculaConsular?: BeneficialOwner["matriculaConsular"]
	nationality?: BeneficialOwner["nationality"]
	address: BeneficialOwner["address"]
	dateOfBirth: BeneficialOwner["dateOfBirth"]
	email: BeneficialOwner["email"]
	phone: BeneficialOwner["phone"]
	percentage?: BeneficialOwner["percentage"]
	close: () => void
	refresh: () => void
	updateBeneficialOwner: UpdateBeneficialOwnerFn
}

export type BeneficialOwnerFormValues = {
	firstName: string
	lastName: string
	ssn: string
	nationality: string
	passport: string
	matriculaConsular: string
	street: string
	street2: string
	city: string
	state: string
	postalCode: string
	country: string
	dateOfBirth: string
	email: string
	phone: string
	percentage: number
	identificationType: string
}

type UpdateBeneficialOwnerResponse = OkDocument<BeneficialOwnerResource> | OkDocument<CustomerBeneficialOwner>

export type UpdateBeneficialOwnerFn = (
	accessToken: string,
	values: BeneficialOwnerFormValues
) => Promise<Result<UpdateBeneficialOwnerResponse, ErrorDocument>>

const closeSuccessfully = (refresh: () => void, close: () => void, addToast: AddToast, delay = 0) => {
	setTimeout(() => {
		addToast("Beneficial Owner Updated Successfully", {appearance: "success"})
		close()
		refresh()
	}, delay)
}

export default function EditBeneficialOwnerModal({
	fullName,
	ssn,
	passport,
	matriculaConsular,
	nationality,
	address,
	dateOfBirth,
	email,
	phone,
	percentage,
	close,
	refresh,
	updateBeneficialOwner,
}: ModalProps) {
	const accessToken = useAccessToken()
	const {addToast} = useToasts()
	const validationSchema = Yup.object().shape(beneficialOwnerValidationSchema)

	const [state, update] = useAsyncResultIdle(updateBeneficialOwner)

	const initialFormValues = buildBeneficialOwnerFormInitialValues({
		fullName,
		ssn,
		passport,
		matriculaConsular,
		nationality,
		address,
		dateOfBirth,
		email,
		phone,
		percentage,
	})

	return (
		<Formik
			initialValues={initialFormValues}
			validationSchema={validationSchema}
			onSubmit={(values) => {
				update(accessToken, values)
			}}
		>
			{({values, handleSubmit, handleChange, setFieldValue, errors, isValid, handleBlur}) => (
				<AsyncResultModal
					title="Edit Beneficial Owner"
					onSubmit={(e: React.FormEvent<HTMLFormElement> | undefined) => {
						handleSubmit(e)
						if (!isValid) {
							focusFirstInvalidField(errors)
						}
					}}
					close={close}
					state={state}
					classname="edit-beneficial-owner-modal"
					buttonClassname="is-success"
					buttonText="Save"
					successText="Updated successfully"
					errorToText={(err: any) => {
						return err.errors[0].detail ? err.errors[0].detail : err.errors[0].title
					}}
					onSuccess={() => closeSuccessfully(refresh, close, addToast)}
					enableClickAway={false}
				>
					<LeftRightSlots
						left={<ModalContentLeftSide values={values} handleChange={handleChange} />}
						right={
							<ModalContentRightSide
								values={values}
								handleChange={handleChange}
								handleBlur={handleBlur}
								setFieldValue={setFieldValue}
							/>
						}
					/>
				</AsyncResultModal>
			)}
		</Formik>
	)
}

export function buildBeneficialOwnerFormInitialValues(attributes: {
	fullName: BeneficialOwner["fullName"]
	ssn?: BeneficialOwner["ssn"]
	passport?: BeneficialOwner["passport"]
	matriculaConsular?: BeneficialOwner["matriculaConsular"]
	nationality?: BeneficialOwner["nationality"]
	address: BeneficialOwner["address"]
	dateOfBirth: BeneficialOwner["dateOfBirth"]
	email: BeneficialOwner["email"]
	phone: BeneficialOwner["phone"]
	percentage?: BeneficialOwner["percentage"]
}): BeneficialOwnerFormValues {
	return {
		firstName: attributes.fullName.first ?? "",
		lastName: attributes.fullName.last ?? "",
		ssn: attributes.ssn ?? "",
		nationality: attributes.nationality ?? "",
		passport: attributes.passport ?? "",
		matriculaConsular: attributes.matriculaConsular ?? "",
		street: attributes.address?.street ?? "",
		street2: attributes.address?.street2 ?? "",
		city: attributes.address?.city ?? "",
		state: attributes.address?.state ?? "",
		postalCode: attributes.address?.postalCode ?? "",
		country: attributes.address?.country ?? US_COUNTRY_CODE,
		dateOfBirth: attributes.dateOfBirth ? moment(attributes.dateOfBirth).format("YYYY-MM-DD") : "",
		email: attributes.email ?? "",
		phone: attributes.phone ? toPhoneNumber(attributes.phone) : "",
		percentage: attributes.percentage ?? 25,
		identificationType: attributes.ssn ? "ssn" : attributes.matriculaConsular ? "matriculaConsular" : "passport",
	}
}
